import { trackEvent, AnalyticsEvents } from '../analytics/instance';
import { create } from 'zustand';

// Per-user beta feature flags, stored locally (per browser). No backend.
const STORAGE_KEY = 'featureFlags';

function load(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed && typeof parsed === 'object') return parsed as Record<string, boolean>;
  } catch {
    // Corrupt value — fall back to empty (all features off).
  }
  return {};
}

function persist(state: Record<string, boolean>) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

interface FeatureFlagsState {
  enabled: Record<string, boolean>;
  setFeature: (key: string, value: boolean) => void;
  toggleFeature: (key: string) => void;
}

export const useFeatureFlagsStore = create<FeatureFlagsState>((set, get) => ({
  enabled: load(),
  setFeature: (key, value) => {
    const next = { ...get().enabled, [key]: value };
    persist(next);
    set({ enabled: next });
    trackEvent(AnalyticsEvents.FEATURE_FLAG_TOGGLED, { key, enabled: value });
  },
  toggleFeature: (key) => get().setFeature(key, get().enabled[key] !== true),
}));

/** Reactive check — re-renders when the flag changes. Default OFF. */
export function useHasFeature(key: string): boolean {
  return useFeatureFlagsStore((s) => s.enabled[key] === true);
}
